import { Col, FlexView, Row, Separator } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import { Table, TableCell } from 'comps';
import Pagination from 'modules/list/pagination';
import Search from 'modules/list/new-search-bar';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Loader, Menu, Popup, TableRow } from 'semantic-ui-react';
import styled from 'styled-components';

import COLORS from '../../../constants/colors';
import { useGetAllEvalPrepCompliance } from 'actions/eval-prep-visibility';
import { MonoText, PageHeader, RegionPicker } from '../common/filters';
import PastRangePicker, {
  defaultPastRange,
  pastRangeToEpochs,
} from '../common/past-range-picker';
import {
  VIOLATION_SEVERITY_HINTS,
  VIOLATION_STATUSES,
  VIOLATION_STATUS_HINTS,
} from '../constants';
import ReverseViolation from './reverse';

const PER_PAGE = 20;

const Container = styled(FlexView)`
  padding: 0 16px;
  width: 100%;
`;
const TableWrapper = styled(Row)`
  margin-top: 16px;
  width: 100%;
`;
// gap on Row is vertical padding, not a flex gap, so the spacing is set here.
const ControlRow = styled(Row)`
  flex-wrap: wrap;
  gap: 12px;
  row-gap: 10px;
  padding: 6px 0 10px;
`;
const Stats = styled(Row)`
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`;
const Stat = styled(Col)`
  border: 1px solid ${COLORS.GRAY_LIGHT};
  border-radius: 6px;
  padding: 8px 14px;
  min-width: 96px;
  flex: 0 0 auto;
`;
// Clamped so one wordy reason cannot stretch every row in the table.
const Reason = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 12px;
  line-height: 16px;
  color: ${COLORS.GRAY};
`;

const Chip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  white-space: nowrap;
  background: ${({ tone }) => tone.bg};
  color: ${({ tone }) => tone.fg};
`;

const VIOLATION_STATUS_ACTIVE = 'ACTIVE';

const STATUS_TONES = {
  ACTIVE: { bg: '#f8ebeb', fg: COLORS.RUSTY_RED },
  REVERSED: { bg: '#f6f6f6', fg: COLORS.GRAY },
};
const SEVERITY_TONES = {
  MINOR: { bg: '#f6f6f6', fg: COLORS.GRAY },
  MILD: { bg: '#f9f0e2', fg: COLORS.YELLOW_4 },
  MODERATE: { bg: '#f8ebeb', fg: COLORS.RUSTY_RED },
  SEVERE: { bg: '#f8ebeb', fg: COLORS.RUSTY_RED },
};

const EvalPrepCompliance = () => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState(null);
  const [search, setSearch] = useState('');
  const [region, setRegion] = useState(null);
  const [range, setRange] = useState(defaultPastRange);
  const [reversing, setReversing] = useState(null);

  const {
    data,
    getAll: getViolations,
    isProcessing,
    error: loadError,
  } = useGetAllEvalPrepCompliance();

  const load = useCallback(() => {
    getViolations({
      page,
      per_page: PER_PAGE,
      ...(status ? { status } : {}),
      ...(search ? { intel_enrollment_id: search } : {}),
      ...(region ? { region } : {}),
      ...pastRangeToEpochs(range),
    });
  }, [getViolations, page, status, search, region, range]);

  useEffect(() => {
    load();
  }, [load]);

  const onRegion = useCallback(next => {
    setRegion(next);
    setPage(1);
  }, []);

  const rows = data?.rows || [];
  const summary = data?.summary || {};
  const canReverse = Boolean(data?.permissions?.can_reverse);
  const total = data?.pagination?.total ?? null;

  return (
    <Container column>
      <PageHeader title="Prep Compliance" />

      <Stats>
        <Stat>
          <Text variant="h6">{summary.active ?? 0}</Text>
          <Text variant="body3" color={COLORS.GRAY}>
            Active
          </Text>
        </Stat>
        <Stat>
          <Text variant="h6">{summary.reversed ?? 0}</Text>
          <Text variant="body3" color={COLORS.GRAY}>
            Reversed
          </Text>
        </Stat>
        <Stat>
          <Text variant="h6">{summary.points_live ?? 0}</Text>
          <Text variant="body3" color={COLORS.GRAY}>
            Points live
          </Text>
        </Stat>
      </Stats>

      <Separator transparent height={12} />
      <Menu pointing secondary>
        <Menu.Item active={!status} onClick={() => setStatus(null)} content="All" />
        {VIOLATION_STATUSES.map(value => (
          <Menu.Item
            key={value}
            active={status === value}
            onClick={() => {
              setStatus(value);
              setPage(1);
            }}
            content={value.replace('_', ' ').toLowerCase()}
          />
        ))}
      </Menu>

      <ControlRow gutter={0} alignItems="center">
        <PastRangePicker value={range} onChange={setRange} />
        <RegionPicker value={region} onChange={onRegion} />
        <Search placeholder="Search by enrollment ID" onSearch={setSearch} />
      </ControlRow>

      <TableWrapper>
        {isProcessing ? (
          <Loader active inline="centered">
            Loading violations...
          </Loader>
        ) : loadError ? (
          <Text variant="body2" color={COLORS.RUSTY_RED}>
            {loadError.message || 'Could not load violations.'}
          </Text>
        ) : rows.length === 0 ? (
          <Text variant="body2" color={COLORS.GRAY}>
            No violations raised for eval prep. They appear once the prep tasks are
            marked compliance-eligible and the coach is in the compliance cohort.
          </Text>
        ) : (
          <Table
            headerCell={[
              { title: 'Coach' },
              { title: 'Student' },
              { title: 'Violation' },
              { title: 'Severity' },
              { title: 'Points' },
              { title: 'Status' },
              { title: 'Reversal' },
              { title: '' },
            ]}
            dataLength={rows.length}
            havePagination
            scrollBodyHeight={
              isProcessing || !rows.length ? '400px' : 'calc(100vh - 320px)'
            }
            showInfo={false}
            showLoader={isProcessing}
            flexBasis="100%"
            noDataMessage="No violations raised for eval prep yet."
          >
            {rows.map(row => (
              <TableRow key={row.id}>
                <TableCell width={3} textTransform="none">
                  {row.teacher_name || '-'}
                  <Separator transparent height={2} />
                  <MonoText>{row.teacher_id}</MonoText>
                </TableCell>
                <TableCell width={3} textTransform="none">
                  <MonoText>{row.intel_enrollment_id || '-'}</MonoText>
                </TableCell>
                <TableCell width={2} textTransform="none">
                  {row.display_name || row.violation_type}
                </TableCell>
                <TableCell width={2}>
                  <Popup
                    inverted
                    size="small"
                    position="top center"
                    content={VIOLATION_SEVERITY_HINTS[row.violation_category]}
                    disabled={!VIOLATION_SEVERITY_HINTS[row.violation_category]}
                    trigger={
                      <Chip
                        tone={
                          SEVERITY_TONES[row.violation_category] || SEVERITY_TONES.MINOR
                        }
                      >
                        {row.violation_category || '—'}
                      </Chip>
                    }
                  />
                </TableCell>
                <TableCell width={1} textTransform="none">
                  <MonoText>{row.points_deducted}</MonoText>
                </TableCell>
                <TableCell width={2}>
                  <Popup
                    inverted
                    size="small"
                    position="top center"
                    content={VIOLATION_STATUS_HINTS[row.status]}
                    disabled={!VIOLATION_STATUS_HINTS[row.status]}
                    trigger={
                      <Chip tone={STATUS_TONES[row.status] || STATUS_TONES.REVERSED}>
                        {row.status || '—'}
                      </Chip>
                    }
                  />
                </TableCell>
                <TableCell width={4} textTransform="none">
                  {row.reversal_reason ? (
                    <>
                      <Reason>{row.reversal_reason}</Reason>
                      <Separator transparent height={2} />
                      <Text variant="body3" color={COLORS.GRAY}>
                        by {row.reversed_by_name || row.reversed_by_actor_id}
                      </Text>
                    </>
                  ) : null}
                </TableCell>
                <TableCell width={2}>
                  {canReverse && row.status === VIOLATION_STATUS_ACTIVE && (
                    <Button size="tiny" basic onClick={() => setReversing(row)}>
                      Reverse
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </Table>
        )}
      </TableWrapper>

      <Separator transparent height={8} />
      <Row gutter={0} justifyContent="center">
        {total > PER_PAGE && (
          <Pagination
            page={page}
            perPage={PER_PAGE}
            count={total}
            onPageChange={setPage}
            shouldShowNewUi
          />
        )}
      </Row>

      {reversing && (
        <ReverseViolation
          violation={reversing}
          closeModal={() => setReversing(null)}
          onReversed={load}
        />
      )}
    </Container>
  );
};

export default EvalPrepCompliance;
